import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { parseEvaluationRunMetadata } from "./evaluationLauncherState"
import type { SimulationTemplateData } from "./types"

interface UseRunMetadataEditorParams {
  initialTemplateData?: SimulationTemplateData
  sourceRunId?: string
}

export function useRunMetadataEditor({
  initialTemplateData,
  sourceRunId,
}: UseRunMetadataEditorParams) {
  const [json, setJson] = useState("")
  const hydratedSourceRunIdRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    if (!sourceRunId) {
      if (hydratedSourceRunIdRef.current) {
        setJson("")
      }
      hydratedSourceRunIdRef.current = undefined
      return
    }

    if (
      hydratedSourceRunIdRef.current &&
      hydratedSourceRunIdRef.current !== sourceRunId
    ) {
      setJson("")
      hydratedSourceRunIdRef.current = undefined
    }

    if (
      initialTemplateData?.sourceRunId !== sourceRunId ||
      hydratedSourceRunIdRef.current === sourceRunId
    ) {
      return
    }

    setJson(
      initialTemplateData.customerMetadata
        ? JSON.stringify(initialTemplateData.customerMetadata, null, 2)
        : "",
    )
    hydratedSourceRunIdRef.current = sourceRunId
  }, [initialTemplateData, sourceRunId])

  const parsed = useMemo(() => parseEvaluationRunMetadata(json), [json])

  const handleChange = useCallback((value: string) => {
    setJson(value)
  }, [])

  const handleBlur = useCallback(() => {
    if (parsed.customerMetadata) {
      setJson(JSON.stringify(parsed.customerMetadata, null, 2))
    }
  }, [parsed.customerMetadata])

  return {
    customerMetadata: parsed.customerMetadata,
    error: parsed.error,
    handleBlur,
    handleChange,
    json,
  }
}
